// frontend/src/hooks/useCopy.tsx
// #1039 / AP-26. The one way a family-facing surface reads editable wording.
//
// `copy('some.slot')` always returns something renderable. There is no loading
// value, no `undefined`, and no empty string anywhere in the return type — the
// seed ships in this bundle, so the worst case is the wording the component
// used to hardcode. What the hook adds on top is the chain in `copyResolve.ts`:
// the published override first, then this browser's bounded last-known-good,
// then the seed.
//
// GUARDRAIL 7 — `ready`. AP-26 promises TRF sixty seconds from publish to
// family screen, and the design forbids a server cache, so the whole budget is
// spent in `COPY_STALE_TIME_MS`. The cost of that freshness is that first paint
// has a network read in front of it, and a surface that paints the seed and
// then swaps in the override half a second later shows a family one wording
// replaced by another mid-read. So the hook reports `ready` and the consumer
// holds its paint until the read has SETTLED — succeeded or failed, either is
// an answer. It is never pending forever: `retry: 0` means one attempt.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { api } from '@/services/api';
import { machineMarkFor } from '@/i18n/machineMark';
import type { CopySlotKey } from '@/lib/copySlots';
import {
  COPY_NAMESPACE,
  COPY_STALE_TIME_MS,
  consumeFallbackCounts,
  interpolateCopy,
  isEnglishLanguage,
  readCopyLkg,
  readFallbackCounts,
  recordFallback,
  resolveCopySlot,
  resolveCopySlotLocalized,
  resolveCopyValues,
  writeCopyLkg,
  type CopyOrigin,
  type CopyPayload,
  type CopyValueFor,
} from '@/lib/copyResolve';

/** The one document PR-C ships. `application-flow` is created by
 *  backend/scripts/seed-copy-documents.mjs. */
export const COPY_DOCUMENT_SLUG = 'application-flow';

export interface CopyBundle {
  /** The resolved wording for a slot, typed by that slot's declared kind, in
   *  the reader's language when the `copy` catalog has a current translation
   *  (#2109 R3-B17). `params` fills a prose slot's `{{name}}` placeholders —
   *  `copy('apply.status.submitted_prefix', { date })`. */
  copy: <K extends CopySlotKey>(key: K, params?: Record<string, unknown>) => CopyValueFor<K>;
  /** False until the read has settled. Hold the paint while it is false. */
  ready: boolean;
  /** Which limb of the chain answered — surfaced for tests and the editor. */
  origin: CopyOrigin;
}

const localStore = (): Storage | null => (typeof window === 'undefined' ? null : window.localStorage);

/**
 * PREVIEW MODE (#1039 PR-D, D-CS-12).
 *
 * The copy editor previews the REAL family screen from DRAFT values. Anything
 * else is a facsimile, and a facsimile is exactly what the mockup admitted to
 * being — it can agree with the component today and drift from it tomorrow,
 * on the surface whose entire job is showing what a family will actually read.
 *
 * A context, not a prop: `InviteRequiredStop` and the application-status view
 * are family-facing components and should not grow a parameter that exists for
 * a staff screen. Inside a provider the hook short-circuits BEFORE the network
 * and BEFORE the cache write — see the two guards in `useCopy` — so a preview
 * never spends a family-facing read and never leaves draft wording in the
 * last-known-good store the API-down path reads from.
 */
const CopyPreviewContext = createContext<Record<string, unknown> | null>(null);

export function CopyPreviewProvider({
  values,
  children,
}: {
  values: Record<string, unknown>;
  children: ReactNode;
}) {
  // Identity-stable per values object; the consumer re-renders when it changes.
  return <CopyPreviewContext.Provider value={values}>{children}</CopyPreviewContext.Provider>;
}

export function useCopy(slug: string = COPY_DOCUMENT_SLUG): CopyBundle {
  const preview = useContext(CopyPreviewContext);
  // #2109 R3-B17. Before R3 the hook never read i18n, so a language switch left
  // every copy slot in English until reload. The language rides in the query
  // key and in the resolver's deps below, so a switch re-resolves.
  // `keepPreviousData` keeps a switch from dropping `ready` back to false: the
  // overrides do not depend on the language, only their translation does.
  const { i18n } = useTranslation(COPY_NAMESPACE);
  const language = i18n.resolvedLanguage ?? i18n.language;

  const query = useQuery({
    queryKey: ['copy', slug, language],
    queryFn: async (): Promise<CopyPayload> => {
      const storage = localStore();
      // D-CS-3.5: the fallback tally rides the read that was going to happen
      // anyway. A beacon of its own would need a pre-auth write endpoint and
      // would be trivially spammable into a fake alarm.
      const pending = readFallbackCounts(storage);
      const response = await api.copy.published(slug, pending);
      consumeFallbackCounts(pending, storage);
      const data = response.data;
      return {
        version_n: data?.version_n ?? 0,
        min_copy_version: data?.min_copy_version ?? 0,
        values: data?.values ?? {},
      };
    },
    // A preview must not spend a family-facing read on a staff screen.
    enabled: preview === null,
    staleTime: COPY_STALE_TIME_MS,
    // One attempt. The global default retries once, which would double the
    // blank a family stares at when the API is down — and the answer to a
    // failed read here is not "try harder", it is "use the cache or the seed".
    retry: 0,
    // #2756 (#2702 class): an errored query refetches on every new observer by
    // default. A reader mounted inside a ready-gated parent would flip `ready`
    // back to false, unmount itself, remount and fetch again: a request loop.
    // A failure is an answer (cache or seed) until the cache entry is
    // collected or an invalidation asks again.
    retryOnMount: false,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  const settled = preview !== null || query.status !== 'pending';
  const payload = query.data ?? null;

  const { values, origin } = useMemo(() => {
    // Preview values are already resolved overrides: what the editor holds,
    // over the seed. There is no cache limb here and there must not be one.
    if (preview) return { values: preview, origin: 'published' as CopyOrigin };
    if (!settled) return { values: {} as Record<string, unknown>, origin: 'seed' as CopyOrigin };
    return resolveCopyValues(payload, readCopyLkg(slug, localStore()), Date.now());
    // `query.dataUpdatedAt` is what changes on a refetch that returns an equal
    // object; without it a re-publish inside the same mount would not re-resolve.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview, settled, payload, slug, query.dataUpdatedAt, language]);

  // One settle, one write, one count. Without the stamp guard React's
  // double-invoked effects would inflate a diagnostic counter in dev, and the
  // signal is supposed to mean "families are seeing stale copy".
  const countedRef = useRef(0);
  const stamp = Math.max(query.dataUpdatedAt, query.errorUpdatedAt);
  useEffect(() => {
    // Never from a preview: draft wording in the LKG store is what a family
    // would see on the next API failure in this browser, and the fallback
    // tally means "families are seeing stale copy".
    //
    // The `preview` term here is DEFENCE IN DEPTH and is currently
    // unreachable — `enabled: preview === null` means a preview never settles,
    // so this returns early regardless. Deleting it changes no test, which is
    // said out loud rather than dressed up as a pin (LESSONS 2026-08-22).
    if (preview || !settled || stamp === 0 || countedRef.current === stamp) return;
    countedRef.current = stamp;
    const storage = localStore();
    if (payload) writeCopyLkg(slug, payload, Date.now(), storage);
    recordFallback(origin, storage);
  }, [preview, settled, stamp, payload, origin, slug]);

  const copy = useCallback(
    function resolve<K extends CopySlotKey>(key: K, params?: Record<string, unknown>): CopyValueFor<K> {
      // The staff preview stays English (R3-B17): staff edit English only
      // (#2117), and the preview shows what they are editing.
      const english = resolveCopySlot(key, values);
      let value: CopyValueFor<K> = english;
      if (!preview && !isEnglishLanguage(language)) {
        value = resolveCopySlotLocalized(key, values, {
          language,
          source: i18n.getResource('en', COPY_NAMESPACE, key),
          target: i18n.getResource(language, COPY_NAMESPACE, key),
        });
        // R3-B16: a translation (never the English fallback) carries the
        // non-production machine mark, as a t() string would. Appended to the
        // end, so a composed or linked span's offsets still hold.
        const mark = value !== english ? machineMarkFor(language, COPY_NAMESPACE, key) : '';
        if (mark) {
          value = (typeof value === 'string'
            ? `${value}${mark}`
            : { ...(value as object), text: `${(value as { text: string }).text}${mark}` }) as CopyValueFor<K>;
        }
      }
      return (typeof value === 'string' ? interpolateCopy(value, params) : value) as CopyValueFor<K>;
    },
    [values, language, i18n, preview],
  );

  return { copy, ready: settled, origin };
}
